import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Badge,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://api.editezy.com/api/poster';

const CreateTrendingPoster = () => {
  const navigate = useNavigate();

  // Form states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [subcategoryName, setSubcategoryName] = useState('');
  const [festivalDate, setFestivalDate] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [title, setTitle] = useState('');
  const [posterLang, setPosterLang] = useState('en');
  const [designData, setDesignData] = useState('');

  // File states
  const [posterImage, setPosterImage] = useState(null);
  const [posterImagePreview, setPosterImagePreview] = useState('');
  const [bgImage, setBgImage] = useState(null);
  const [bgImagePreview, setBgImagePreview] = useState('');
  const [overlayImages, setOverlayImages] = useState([]);
  const [overlayImagePreviews, setOverlayImagePreviews] = useState([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [fetchingSubcategories, setFetchingSubcategories] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setFetchingCategories(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/categories`);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setFetchingCategories(false);
    }
  };

  // Fetch subcategories when category is selected
  const fetchSubcategories = async (categoryId) => {
    setFetchingSubcategories(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/childcategory/${categoryId}`);
      if (response.data.success) {
        setSubcategories(response.data.childCategories);
      }
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setError('Failed to load subcategories');
    } finally {
      setFetchingSubcategories(false);
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedSubcategory(''); // Reset subcategory selection
    setSubcategories([]); // Clear subcategories

    const selectedCat = categories.find(cat => cat._id === categoryId);
    if (selectedCat) {
      setCategoryName(selectedCat.name);
      // Fetch subcategories for selected category
      if (categoryId) {
        fetchSubcategories(categoryId);
      }
    }
  };

  // Handle subcategory selection
  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    setSelectedSubcategory(subcategoryId);
    
    const selectedSub = subcategories.find(sub => sub._id === subcategoryId);
    if (selectedSub) {
      setSubcategoryName(selectedSub.name);
    }
  };

  // Handle poster image selection
  const handlePosterImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPosterImage(file);
      setPosterImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle background image selection
  const handleBgImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBgImage(file);
      setBgImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle multiple overlay images selection
  const handleOverlayImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setOverlayImages([...overlayImages, ...files]);

      const previews = files.map(file => URL.createObjectURL(file));
      setOverlayImagePreviews([...overlayImagePreviews, ...previews]);
    }
  };

  // Remove overlay image
  const removeOverlayImage = (index) => {
    const updatedImages = overlayImages.filter((_, i) => i !== index);
    const updatedPreviews = overlayImagePreviews.filter((_, i) => i !== index);

    setOverlayImages(updatedImages);
    setOverlayImagePreviews(updatedPreviews);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!selectedCategory) {
      setError('Please select a category');
      return;
    }

    if (!selectedSubcategory) {
      setError('Please select a subcategory');
      return;
    }

    // if (!subcategoryName.trim()) {
    //   setError('Subcategory name is required');
    //   return;
    // }

    // if (!festivalDate) {
    //   setError('Festival date is required');
    //   return;
    // }

    // if (!title.trim()) {
    //   setError('Title is required');
    //   return;
    // }

    if (!posterImage) {
      setError('Poster image is required');
      return;
    }

    // if (!bgImage) {
    //   setError('Background image is required');
    //   return;
    // }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    // Prepare form data
    const formData = new FormData();
    formData.append('name', subcategoryName.trim());
    formData.append('categoryName', categoryName);
    formData.append('festivalDate', festivalDate);
    formData.append('description', description);
    formData.append('tags', tags);
    formData.append('email', email);
    formData.append('mobile', mobile);
    formData.append('title', title.trim());
    formData.append('posterlang', posterLang);
    formData.append('designData', designData);
    formData.append('posterImage', posterImage);
    formData.append('bgImage', bgImage);
    formData.append('isTrending', 'true');

    // Append multiple overlay images
    overlayImages.forEach((image) => {
      formData.append('overlayImages', image);
    });
    
    try {
      // Use the selected subcategory ID in the API endpoint
      const response = await axios.post(
        `${API_BASE_URL}/subcategori/${selectedCategory}/${selectedSubcategory}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setSuccess('Poster created successfully!');

        // Reset form after successful submission
        setTimeout(() => {
          resetForm();
          navigate('/trending-posters'); // Navigate to poster list page
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating poster:', err);
      const errorMsg = err.response?.data?.message || 'Failed to create poster. Please try again.';
      setError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setSelectedCategory('');
    setCategoryName('');
    setSelectedSubcategory('');
    setSubcategoryName('');
    setSubcategories([]);
    setFestivalDate('');
    setDescription('');
    setTags('');
    setEmail('');
    setMobile('');
    setTitle('');
    setPosterLang('en');
    setDesignData('');
    setPosterImage(null);
    setPosterImagePreview('');
    setBgImage(null);
    setBgImagePreview('');
    setOverlayImages([]);
    setOverlayImagePreviews([]);
    setError('');
  };

  // Example design data template
  const loadDesignDataExample = () => {
    const exampleData = {
      textSettings: {
        name: "John Doe",
        email: "john@example.com",
        phone: "9876543210"
      },
      overlaySettings: {
        overlays: [
          {
            x: 10,
            y: 20,
            width: 100,
            height: 50,
            type: "text",
            content: "Special Offer"
          },
          {
            x: 150,
            y: 200,
            width: 80,
            height: 40,
            type: "image",
            opacity: 0.8
          }
        ]
      },
      colorScheme: {
        primary: "#FF5722",
        secondary: "#FFC107",
        text: "#FFFFFF"
      }
    };
    setDesignData(JSON.stringify(exampleData, null, 2));
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <CardBody>
              <CardTitle tag="h2" className="text-primary mb-4">
                Create Trending Poster
              </CardTitle>

              {/* Alerts */}
              {error && (
                <Alert color="danger" toggle={() => setError('')} className="mb-3">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert color="success" toggle={() => setSuccess('')} className="mb-3">
                  {success}
                </Alert>
              )}

              {/* Form */}
              <Form onSubmit={handleSubmit}>
                <Row>
                  {/* Basic Information */}
                  <Col md={6}>
                    <h4 className="mb-3">Basic Information</h4>

                    <FormGroup>
                      <Label for="category">Select Category *</Label>
                      <Input
                        id="category"
                        type="select"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        disabled={fetchingCategories}
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map((category) => (
                          <option key={category._id} value={category._id}>
                            {category.name}
                          </option>
                        ))}
                      </Input>
                      {fetchingCategories && <Spinner size="sm" className="mt-2" />}
                    </FormGroup>

                    <FormGroup>
                      <Label for="subcategory">Select Subcategory *</Label>
                      <Input
                        id="subcategory"
                        type="select"
                        value={selectedSubcategory}
                        onChange={handleSubcategoryChange}
                        disabled={!selectedCategory || fetchingSubcategories}
                        required
                      >
                        <option value="">Select a subcategory</option>
                        {subcategories.map((subcategory) => (
                          <option key={subcategory._id} value={subcategory._id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </Input>
                      {fetchingSubcategories && <Spinner size="sm" className="mt-2" />}
                      {selectedCategory && subcategories.length === 0 && !fetchingSubcategories && (
                        <small className="text-muted">No subcategories found for this category</small>
                      )}
                    </FormGroup>

                    {/* <FormGroup>
                      <Label for="subcategoryName">Poster Title </Label>
                      <Input
                        id="subcategoryName"
                        type="text"
                        value={subcategoryName}
                        onChange={(e) => setSubcategoryName(e.target.value)}
                        placeholder="e.g., Monday, Weekend Special, Holiday Offer" 
                      />
                    </FormGroup> */}

                    {/* <FormGroup>
                      <Label for="festivalDate">Festival Date </Label>
                      <Input
                        id="festivalDate"
                        type="date"
                        value={festivalDate}
                        onChange={(e) => setFestivalDate(e.target.value)}
                      />
                    </FormGroup> */}

                    {/* <FormGroup>
                      <Label for="title">Title </Label>
                      <Input
                        id="title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter poster title"
                      />
                    </FormGroup> */}

                    {/* <FormGroup>
                      <Label for="description">Description</Label>
                      <Input
                        id="description"
                        type="textarea"
                        rows="3"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter detailed description"
                      />
                    </FormGroup> */}

                    {/* <FormGroup>
                      <Label for="tags">Tags</Label>
                      <Input
                        id="tags"
                        type="text"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder="e.g., smartphone,5g,android,ios (comma separated)"
                      />
                      <small className="text-muted">
                        Separate tags with commas
                      </small>
                    </FormGroup> */}
                  </Col>

                  {/* Contact Information */}
                  <Col md={6}>
                    {/* <h4 className="mb-3">Contact Information</h4>

                    <FormGroup>
                      <Label for="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="sales@example.com"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="mobile">Mobile Number</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="9876543210"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="posterLang">Poster Language</Label>
                      <Input
                        id="posterLang"
                        type="select"
                        value={posterLang}
                        onChange={(e) => setPosterLang(e.target.value)}
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="bn">Bengali</option>
                        <option value="te">Telugu</option>
                        <option value="mr">Marathi</option>
                        <option value="ta">Tamil</option>
                        <option value="gu">Gujarati</option>
                        <option value="kn">Kannada</option>
                        <option value="ml">Malayalam</option>
                      </Input>
                    </FormGroup> */}

                    {/* <FormGroup>
                      <Label for="designData">Design Data (JSON)</Label>
                      <Input
                        id="designData"
                        type="textarea"
                        rows="6"
                        value={designData}
                        onChange={(e) => setDesignData(e.target.value)}
                        placeholder='{"textSettings":{},"overlaySettings":{}}'
                      />
                      <Button
                        type="button"
                        color="link"
                        size="sm"
                        onClick={loadDesignDataExample}
                        className="mt-1 p-0"
                      >
                        Load Example JSON
                      </Button>
                    </FormGroup> */}
                  </Col>
                </Row>

                {/* Image Uploads */}
                <Row className="mt-4">
                  <Col md={6}>
                    <h4 className="mb-3">Images</h4>

                    <FormGroup>
                      <Label for="posterImage">Poster Image *</Label>
                      <Input
                        id="posterImage"
                        type="file"
                        accept="image/*"
                        onChange={handlePosterImageChange}
                        required
                      />
                      {posterImagePreview && (
                        <div className="mt-2">
                          <img
                            src={posterImagePreview}
                            alt="Poster Preview"
                            style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                          />
                        </div>
                      )}
                    </FormGroup>

                    {/* <FormGroup>
                      <Label for="bgImage">Background Image </Label>
                      <Input
                        id="bgImage"
                        type="file"
                        accept="image/*"
                        onChange={handleBgImageChange}
                      />
                      {bgImagePreview && (
                        <div className="mt-2">
                          <img
                            src={bgImagePreview}
                            alt="Background Preview"
                            style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px' }}
                          />
                        </div>
                      )}
                    </FormGroup> */}

                    {/* <FormGroup>
                      <Label for="overlayImages">Overlay Images (Multiple)</Label>
                      <Input
                        id="overlayImages"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleOverlayImagesChange}
                      />
                      <small className="text-muted">
                        You can select multiple overlay images
                      </small>
                    </FormGroup> */}
                  </Col>

                  {/* Overlay Images Preview */}
                  {overlayImagePreviews.length > 0 && (
                    <Col md={6}>
                      <h4 className="mb-3">Overlay Images Preview</h4>
                      <ListGroup>
                        {overlayImagePreviews.map((preview, index) => (
                          <ListGroupItem key={index} className="d-flex justify-content-between align-items-center">
                            <img
                              src={preview}
                              alt={`Overlay ${index + 1}`}
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                            <span className="ms-3 flex-grow-1">
                              Overlay Image {index + 1}
                            </span>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => removeOverlayImage(index)}
                            >
                              Remove
                            </Button>
                          </ListGroupItem>
                        ))}
                      </ListGroup>
                    </Col>
                  )}
                </Row>

                {/* Form Actions */}
                <Row className="mt-4">
                  <Col className="d-flex justify-content-end gap-2">
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => navigate('/trending-posters')}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="reset"
                      color="warning"
                      onClick={resetForm}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Creating...
                        </>
                      ) : (
                        'Create Poster'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateTrendingPoster;